import React from 'react';
import { Box, Paper, Typography, LinearProgress } from '@mui/material';
import { Autorenew as RunningIcon } from '@mui/icons-material';

const toMillis = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * FH-272: one background run in Atom's chat (a large build part). Says what is being done and how far it is, in
 * warehouse words: the parts of the build still under way with their counts ("Locations 37 of 124"), the step it is
 * on, and a progress bar. The user can keep talking to Atom while it shows; a message follows in the chat when it ends.
 */
export default function BackgroundRunCard({ run, planStatus, colors }) {
  const label = run.metadata?.label || 'Working in the background';
  const started = toMillis(run.startedAt || run.createdAt);
  const total = Number(run.progressTotal) || 0;
  const current = Math.min(Number(run.progressCurrent) || 0, total);
  const percent = total > 0 ? Math.round((current * 100) / total) : null;
  const phases = (planStatus?.phases || [])
    .filter((p) => Number(p.records) > 0 && Number(p.recordsBuilt) < Number(p.records))
    .map((p) => `${p.phase} ${Number(p.recordsBuilt)} of ${Number(p.records)}`);
  return (
    <Paper
      elevation={0}
      data-testid="background-run"
      role="status"
      aria-label={label}
      sx={{
        mb: 2,
        p: 1.5,
        borderLeft: '3px solid #22c55e',
        bgcolor: colors?.surfaceElevated || 'transparent',
        border: `1px solid ${colors?.border || 'rgba(127,127,127,0.3)'}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <RunningIcon fontSize="small" sx={{ color: '#22c55e', animation: 'bgRunSpin 2s linear infinite' }} />
        <Typography variant="subtitle2" sx={{ color: colors?.textPrimary, fontWeight: 600 }}>{label}</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: colors?.textSecondary, fontSize: '0.85rem' }}>
        {phases.length ? `${phases.join(' · ')}. ` : ''}
        Running in the background{started ? ` since ${new Date(started).toLocaleTimeString()}` : ''}: you can keep
        talking to Atom, and a message follows here when it is done.
      </Typography>
      {run.progressMessage && (
        <Typography variant="caption" sx={{ color: colors?.textMuted || colors?.textSecondary, display: 'block', mt: 0.5 }}>
          Now: {run.progressMessage}
        </Typography>
      )}
      <LinearProgress
        variant={percent == null ? 'indeterminate' : 'determinate'}
        value={percent == null ? undefined : percent}
        aria-label={`${label} progress`}
        sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: 'rgba(34,197,94,0.15)', '& .MuiLinearProgress-bar': { bgcolor: '#22c55e' } }}
      />
      <style>{'@keyframes bgRunSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }'}</style>
    </Paper>
  );
}
